import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeftLong, FaBarsStaggered } from "react-icons/fa6";
import axios from "axios";
import Loader from "../../components/Loader";
import Sidebar from "./Sidebar";
import AdminNav from "./AdminNav";
import {
  Card,
  Table,
  Button,
  Statistic,
  Row,
  Col,
  Divider,
  Typography,
  Modal,
  Form,
  Input,
  Space,
  Select,
} from "antd";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { toast } from "react-toastify";
import { formatNumber } from "../../utils/formateNum";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const FundDetails = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fund, setFund] = useState(null);
  const [navData, setNavData] = useState([]);
  const [fundClasses, setFundClasses] = useState([]);
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("admintoken");

  // Deposit modal state
  const [isDepositModalVisible, setIsDepositModalVisible] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [depositForm] = Form.useForm();

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const fetchfundClasses = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/classes/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFundClasses(data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchFundDetails = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/fund/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFund(data.fund);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNavData = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/nav/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNavData(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchEnrolledUsers = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}/api/v1/fund/enroll-user/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setEnrolledUsers(data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFundDetails();
    fetchNavData();
    fetchEnrolledUsers();
    fetchfundClasses();
  }, [id]);

  // Chart data remains raw—formatting is applied in tables/statistics:
  const navChartData = {
    labels: navData.map((item) => {
      const d = new Date(item.createdAt);
      return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
    }),
    datasets: [
      {
        label: "NAV Value",
        data: navData.map((item) => item.NAV),
        borderColor: "rgb(75, 192, 192)",
        tension: 0.1,
      },
    ],
  };

  const userDistributionData = {
    labels: enrolledUsers.map((user) => user.user?.name || "Unknown"),
    datasets: [
      {
        label: "Investment Amount",
        data: enrolledUsers.map((user) => user.balance),
        backgroundColor: [
          "rgba(255, 99, 132, 0.2)",
          "rgba(54, 162, 235, 0.2)",
          "rgba(255, 206, 86, 0.2)",
          "rgba(75, 192, 192, 0.2)",
          "rgba(153, 102, 255, 0.2)",
          "rgba(255, 159, 64, 0.2)",
        ],
        borderColor: [
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const navColumns = [
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        const hours = String(d.getHours()).padStart(2, "0");
        const minutes = String(d.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day} ${hours}:${minutes}`;
      },
    },
    {
      title: "NAV Value",
      dataIndex: "NAV",
      key: "NAV",
      render: (nav) => formatNumber(nav),
    },
  ];

  // 2. Investor Details table: format balances & deposits, plus Deposit button
  const usersColumns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      render: (user) => user?.name || "N/A",
    },
    {
      title: "Email",
      dataIndex: "user",
      key: "email",
      render: (user) => user?.email || "N/A",
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      render: (cls) => cls?.class || "N/A",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (bal) => formatNumber(bal),
    },
    {
      title: "Deposit Balance",
      dataIndex: "depositBalance",
      key: "depositBalance",
      render: (dep) => formatNumber(dep),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button onClick={() => showDepositModal(record)}>Deposit</Button>
      ),
    },
  ];

  // Show deposit modal for a given enrollment
  const showDepositModal = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setIsDepositModalVisible(true);
  };

  const handleDepositCancel = () => {
    setIsDepositModalVisible(false);
    depositForm.resetFields();
  };

  const handleDepositSubmit = async (values) => {
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API}/api/v1/fund/deposit-balance/${selectedEnrollment._id}`,
        { amount: values.amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Balance deposited successfully");
      fetchEnrolledUsers();
      handleDepositCancel();
    } catch (error) {
      console.error(error);
      toast.error("Failed to deposit balance");
    } finally {
      setLoading(false);
    }
  };

  // 3. Classes table unchanged
  const classColumns = [
    {
      title: "Class Name",
      dataIndex: "class",
      key: "class",
    },
    {
      title: "CUSIP",
      dataIndex: "cusip",
      key: "cusip",
    },
    {
      title: "ISIN",
      dataIndex: "isin",
      key: "isin",
    },
    {
      title: "Inception Date",
      dataIndex: "inception",
      key: "inception",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "NAV Calculation",
      dataIndex: "navCalculation",
      key: "navCalculation",
    },
    {
      title: "Subscription",
      dataIndex: "subscription",
      key: "subscription",
    },
    {
      title: "Redemption",
      dataIndex: "redemption",
      key: "redemption",
    },
    {
      title: "Redemption Fee (%)",
      dataIndex: "redemptionFee",
      key: "redemptionFee",
      render: (fee) => (fee * 100).toFixed(2) + "%",
    },
    {
      title: "Total Profit",
      dataIndex: "totalProfit",
      key: "totalProfit",
      render: (profit) => formatNumber(profit),
    },
  ];

  if (!fund) return <Loader />;

  return (
    <div className="wrapper d-flex align-items-stretch">
      {loading && <Loader />}
      <Sidebar sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="content" className="px-2">
        <button
          type="button"
          id="openSidebar"
          onClick={toggleSidebar}
          className="bars-btn"
        >
          <FaBarsStaggered />
        </button>
        <AdminNav />

        <Button
          type="link"
          onClick={() => navigate(-1)}
          icon={<FaArrowLeftLong />}
          style={{ marginBottom: 16 }}
        >
          Back to Funds
        </Button>

        <Typography.Title level={2}>{fund.name}</Typography.Title>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic title="Fund Code" value={fund.code} precision={0} />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Current Status"
                value={fund.status}
                precision={0}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic title="Fund Type" value={fund.type} precision={0} />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Management Fee"
                value={(fund.managementFee * 100).toFixed(2)}
                suffix="%"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Performance Fee"
                value={(fund.performanceFee * 100).toFixed(2)}
                suffix="%"
                precision={2}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Hurdle Rate"
                value={(fund.hurdleRate * 100).toFixed(2)}
                suffix="%"
                precision={2}
              />
            </Card>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Fund Profit"
                value={formatNumber(fund.totalProfit)}
                prefix="$"
                precision={2}
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">{fund.name} Classes</Divider>
        <Table
          columns={classColumns}
          dataSource={fundClasses}
          rowKey="_id"
          pagination={{ pageSize: 5 }}
        />

        <Divider orientation="left">NAV Performance</Divider>
        <Card style={{ marginBottom: 24 }}>
          <Line data={navChartData} />
        </Card>

        <Divider orientation="left">Latest NAV Values</Divider>
        <Table
          columns={navColumns}
          dataSource={navData}
          rowKey="_id"
          pagination={{ pageSize: 5 }}
          style={{ marginBottom: 24 }}
        />

        <Divider orientation="left">Investor Distribution</Divider>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Card>
              <Pie data={userDistributionData} />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Investor Statistics">
              <Statistic
                title="Total Investors"
                value={enrolledUsers.length}
                precision={0}
              />
              <Statistic
                title="Total Assets Under Management"
                value={enrolledUsers.reduce(
                  (sum, user) => sum + user.balance,
                  0
                )}
                prefix="$"
                formatter={(val) => formatNumber(val)}
                style={{ marginTop: 16 }}
              />
            </Card>
          </Col>
        </Row>

        <Divider orientation="left">Investor Details</Divider>
        <Table
          columns={usersColumns}
          dataSource={enrolledUsers}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
        />
      </div>

      {/* Deposit Balance Modal */}
      <Modal
        title={`Deposit to ${
          selectedEnrollment?.user?.name || "User"
        } (Class: ${selectedEnrollment?.class?.class || ""})`}
        open={isDepositModalVisible}
        onCancel={handleDepositCancel}
        footer={null}
      >
        <Form
          form={depositForm}
          onFinish={handleDepositSubmit}
          layout="vertical"
        >
          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true, message: "Please enter deposit amount" }]}
          >
            <Input type="number" min="0" step="0.01" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Deposit
              </Button>
              <Button onClick={handleDepositCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FundDetails;
